import { useQuery } from "react-query";
import axiosInstance from "../../services/axiosInstance";

interface TimeslotCountResponse {
  [timeslot: string]: number;
}

interface UseTimeslotCountParams {
  productId?: number;
  destDate?: string; // format: YYYYMMDD
}

const fetchTimeslotCount = async ({
  productId,
  destDate,
}: UseTimeslotCountParams): Promise<TimeslotCountResponse> => {
  const response = await axiosInstance.get(
    "/booking-value-calendar/timeslot-count",
    {
      params: { productId, destDate },
    },
  );
  return response.data.result;
};

export const useTimeslotCount = (
  { productId, destDate }: UseTimeslotCountParams,
  enabled = true,
) => {
  return useQuery({
    queryKey: ["timeslot-count", productId, destDate],
    queryFn: () => fetchTimeslotCount({ productId, destDate }),
    enabled: !!productId && !!destDate && enabled,
  });
};

interface UseSlotRemainingParams extends UseTimeslotCountParams {
  maxNumberOfGuests?: number;
  timeSlot?: string;
}

export const useSlotRemaining = ({
  productId,
  destDate,
  maxNumberOfGuests,
  timeSlot,
}: UseSlotRemainingParams): {
  slotRemaining: number;
  slotRemainingMap?: Record<string, number>;
} => {
  const timeslotCount = useTimeslotCount(
    { productId, destDate },
    !!maxNumberOfGuests,
  );
  if (!maxNumberOfGuests) {
    return {
      slotRemaining: -1,
      slotRemainingMap: {},
    };
  }
  const slotRemainingMap: Record<string, number> = {};
  if (timeslotCount.data) {
    for (const [key, value] of Object.entries(timeslotCount.data)) {
      slotRemainingMap[key] = Math.max(maxNumberOfGuests - value, 0);
    }
  }
  const remaining =
    (typeof maxNumberOfGuests === "number"
      ? Math.max(
          maxNumberOfGuests - +(timeslotCount.data?.[timeSlot ?? ""] ?? 0),
          0,
        )
      : undefined) ?? -1;

  return {
    slotRemaining: remaining,
    slotRemainingMap,
  };
};
